import './style.scss';
import {t} from "i18next";
import {usePublicEndpoints} from "../../../lib/api/usePublicEndpoints";
import {useEffect, useState} from "react";
import FormButton from "../../generic/buttons/main";
import {localizeFullDate} from "../../../lib/utils/format";
import Spinner from "../../generic/Spinner";
import {pluralizeWord} from "../../../lib/utils/helpers";
import {useGroupByYear} from "../../../lib/utils/hooks/useGroupByYear";
import {useInfiniteScroll} from "../../../lib/utils/hooks/useInfiniteScroll";

const calcInitialLimit = () => {
    const availableHeight = window.innerHeight - 180;
    return Math.floor(availableHeight / 66);
}

const EventsProfile = () => {
    const { getUserOrders } = usePublicEndpoints();

    const [upcomingOrders, setUpcomingOrders] = useState([]);
    const [pastOrders, setPastOrders] = useState([]);

    const [pageUpcomingOrders, setPageUpcomingOrders] = useState(0);
    const [pagePastOrders, setPagePastOrders] = useState(0);

    const [hasMoreUpcoming, setHasMoreUpcoming] = useState(true);
    const [hasMorePast, setHasMorePast] = useState(true);

    const [isLoading, setIsLoading] = useState(false);
    const [phase, setPhase] = useState("future");

    const loadOrders = async (type, limit) => {
        if (isLoading) return;
        setIsLoading(true);

        const page = type === 'future' ? pageUpcomingOrders : pagePastOrders;
        const res = await getUserOrders({
            type: type === 'future' ? 'future' : 'past',
            _limit: limit || 10,
            _start: page * (limit || 10)
        });
        const newOrders = res?.data || [];

        if (type === 'future') {
            setUpcomingOrders(prev => [...prev, ...newOrders]);
            setPageUpcomingOrders(prev => prev + 1);
            if (newOrders.length < 10) {
                setHasMoreUpcoming(false);
                setPhase('past');
            }
        } else {
            setPastOrders(prev => [...prev, ...newOrders]);
            setPagePastOrders(prev => prev + 1);
            if (newOrders.length < (limit || 10)) setHasMorePast(false);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        loadOrders('future', calcInitialLimit());
    }, [])

    const sentinelRef = useInfiniteScroll(() => {
        if (!isLoading) {
            if (phase === 'future' && hasMoreUpcoming) {
                loadOrders('future', 10);
            } else if (phase === 'past' && hasMorePast) {
                loadOrders('past', 10);
            }
        }
    }, [phase, hasMoreUpcoming, hasMorePast, isLoading]);

    const {
        groupedData: groupedUpcomingOrders,
        hasData: hasUpcoming
    } = useGroupByYear(upcomingOrders);

    const {
        groupedData: groupedPastOrders,
        hasData: hasPast
    } = useGroupByYear(pastOrders);

    const renderEventsByYear = groupedOrders => {
        return Object.entries(groupedOrders)
            .sort((a, b) => b[0] - a[0])
            .map(([year, events]) => (
                <div key={year}>
                    <div className="event-block__year">{year}</div>
                    {events.map((order, i) => {
                        const dotSeparator = ' • ';
                        const isInstallment = order.is_installment;
                        const { event_name, start_date, start_time, base_name } = order.product_data;
                        let discountLabel = '';
                        discountLabel = order.certificate_applied ? t('profile.events.certificateApplied') : '';
                        discountLabel = order.promocode_applied ? t('profile.events.promocodeApplied') : '';
                        const restToPay = order.discounted_value - order.paid_value;
                        const price = isInstallment ? order.discounted_value : order.paid_value;
                        const isRestToPay = restToPay > 0 && !isInstallment;

                        return (
                            <div key={i} className="event-block__item">
                                <div className="event-block__info">
                                    <div className="event-block__name">
                                        {event_name}
                                        {base_name && `, ${base_name}`}
                                    </div>
                                    <div className="event-block__details">
                                        {localizeFullDate(start_date)}
                                        {start_time && (
                                            <>{dotSeparator + start_time.slice(0, -3)}</>
                                        )}
                                        {dotSeparator + order.paid_for_length + ' ' + pluralizeWord(t('profile.events.place'), order.paid_for_length)}
                                    </div>
                                </div>
                                <div class="event-block__payment">
                                    <div className="event-block__price">{price}&nbsp;₽</div>
                                    <div className="event-block__status">
                                        {discountLabel}
                                        {isRestToPay && (
                                            <>{dotSeparator + t('profile.events.remainingPayment', { amount: restToPay})}</>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            ))
    }

    return (
        <>
            <h1 className='profile-title'>{t('profile.events.blockTitle')}</h1>
            {!upcomingOrders.length && !pastOrders.length &&
                <>
                    <p>{t('profile.events.noPurchases')}</p>
                    <FormButton
                        value={t('profile.events.chooseSomething')}
                        type="button"
                        className="small-button sentence-case"
                        onClick={() => window.location.href = t('profile.events.chooseSomethingLink')}
                    />
                </>
            }

            {hasUpcoming && (
                <div className="event-block event-block__upcoming">
                    <div className="event-block__title">{t('profile.events.upcomingEvents')}</div>
                    <div className="event-block__wrap">
                        {renderEventsByYear(groupedUpcomingOrders)}
                    </div>
                </div>
            )}

            {hasUpcoming && hasPast && (
                <div className="divider no-margin mt-5 mb-5"></div>
            )}

            {hasPast && (
                <div className="event-block event-block__past">
                    <div className="event-block__title">{t('profile.events.pastEvents')}</div>
                    <div className="event-block__wrap">
                        {renderEventsByYear(groupedPastOrders)}
                    </div>
                </div>
            )}

            {isLoading && (
                <Spinner/>
            )}

            <div ref={sentinelRef}></div>
        </>
    )
}

export default EventsProfile;